import React from "react";
import ReactECharts from "echarts-for-react";
import _ from "lodash";
import PropTypes from 'prop-types';
import { getYAxisIndex } from './Helpers.js';

const VerticalStack = (props) => {
    const {
        barColors,
barLabel,
ChartData,
color,
displayData,
events,
isScroll,
isScrollEndLimit,
isScrollStartLimit,
scrollBarHeight,
labelConfig,
legendShow,
onChartClick,
scrollBarBottomAlign,
spaceBottom,
spaceLeft,
spaceRight,
spaceTop,
toolTipConfig,
xAxisData,
xAxisName,
yAxisName,
otherZoomProperty,
maxXaxisLabelLength,
isTruncateXaxisLabel,
barWidth,
axisLine,
    } = props;
    //
    const formatYAxis = (value) => {
        if (value >= 1_000_000_000_000) {return `${Math.floor(value / 1_000_000_000_000)}T`;} // Trillions
        if (value >= 1_000_000_000) {return `${Math.floor(value / 1_000_000_000)}B`;} // Billions
        if (value >= 1_000_000) {return `${Math.floor(value / 1_000_000)}M`;} // Millions
        if (value >= 1_000) {return `${Math.floor(value / 1_000)}K`;} // Thousands
        return value; // Keep as a number
    };
    
    // Apply formatYAxis to each value in ChartData
    const formattedValues =  Array.isArray(ChartData)? ChartData.flat().map(value => formatYAxis(Math.round(value))) : [];

    // Get maxDigits based on the formatted string lengths
    const maxDigits = Math.max(...formattedValues.map(value => value.toString().length));

    const nameGapYAxis = (maxDigits) => {
        let nameGap = 22; // Default
        if (maxDigits <= 2) {nameGap = 30;}
        else if (maxDigits === 3) {nameGap = 33;}
        else if (maxDigits === 4) {nameGap = 45;}
        else if (maxDigits === 5) {nameGap = 55;}
        else if (maxDigits === 6) {nameGap = 66;}
        else if (maxDigits >= 7) {nameGap = 77;}
        return nameGap;
    };
    const getSeriesData = () => {
        const series = displayData.map((name, sid) => ({
            name,
            type: 'bar',
            stack: 'total',
            barWidth: barWidth ? barWidth :'60%',
            itemStyle: { color: barColors[sid] },
            label:{
                position:"top",
                show: barLabel ? barLabel : false,
                textStyle: {
                    color:"black",
                    fontSize: 12,          },
                ...labelConfig,
            },
            data: ChartData[sid] || []

        }));
        return series;
    };
    // Chart options
    const getOption = () => {
        let options = {
            legend: {
                icon: "circle",
                show: legendShow ? legendShow : true,
                type: "scroll",
                orient: "horizontal",
                left: "left",
            },
            tooltip: { show: true, trigger: "item", ...toolTipConfig },
            grid: {
                left: spaceLeft ? spaceLeft : 60,
                right: spaceRight ? spaceRight : 30,
                top: spaceTop ? spaceTop : 69,
                bottom: spaceBottom ? spaceBottom : 45
            },
            yAxis : {
                show: true,
                type: "value",
                ...getYAxisIndex(),
                name: yAxisName,
                nameLocation: "middle",
                nameGap: nameGapYAxis(maxDigits),
                axisLine: { show: axisLine ? axisLine : false },
                axisLabel: {
                    formatter: formatYAxis, // Apply the formatted Y-axis values
                },
            },
            xAxis: {

                name: xAxisName,
                nameLocation: "middle",
                nameGap: 25,
                type: "category",
                data: xAxisData,
                axisLabel: {
                formatter: (value) =>
                    isTruncateXaxisLabel && value.length > maxXaxisLabelLength
                        ? value.slice(0, maxXaxisLabelLength) + '...'
                        : value
            }
            },
            series: getSeriesData()
        };
        if (isScroll) {
            options.dataZoom = [
                {
                    type: 'slider',
                    start: isScrollStartLimit ? isScrollStartLimit : 0,
                    end: isScrollEndLimit ? isScrollEndLimit : 30,
                    height: scrollBarHeight ? scrollBarHeight : 15,
                    bottom: scrollBarBottomAlign ? scrollBarBottomAlign : 15,
                    showDetail: false,
                    handleStyle: {
                        color: [0],
                    },
                    dataBackground: {
                        lineStyle: {
                            color: color[0]  // Set the data background color
                        },
                        areaStyle: {
                            color: 'rgba(255, 0, 0, 0.1)'  // Set the data background area color
                        }
                    },
                    ...otherZoomProperty
                },
                {
                    type: 'inside',  // Inside zoom
                    start: 0,
                    end: 30,
                    ...otherZoomProperty
                },
            ];
        }
        if (isScroll) {
            options.dataZoom = [
                {
                    type: 'slider',
                    start: isScrollStartLimit ? isScrollStartLimit : 0,
                    end: isScrollEndLimit ? isScrollEndLimit : 30,
                    height: 15,
                    bottom: scrollBarBottomAlign ? scrollBarBottomAlign : -5,
                    showDetail: false,
                    handleStyle: {
                        color: color[0],
                    },
                    dataBackground: {
                        lineStyle: {
                            color: color[0]  // Set the data background color
                        },
                        areaStyle: {
                            color: 'rgba(255, 0, 0, 0.1)'  // Set the data background area color
                        }
                    },
                    ...otherZoomProperty
                },
                {
                    type: 'inside',  // Inside zoom
                    start: 0,
                    end: 30,
                    ...otherZoomProperty
                },
            ];
        }
        return options;
    };
    const handleChartClick = (params) => {
        if (onChartClick) {
            onChartClick(params);
        }
    };
    let chartInstance = null;

    const handleChartReady = (chart) => {
        chartInstance = chart;

    };
    const handleLegendSelectChanged = (params) => {
        const selectedValues = Object.values(params.selected);
        const allFalse = selectedValues.every(value => value === false);
        if (allFalse) {
            const YFilledHide = {
              yAxis: {
                type: "value",
                name: yAxisName,
                nameLocation: "middle",
                nameGap: nameGapYAxis(maxDigits),
                show: false,
              },
            };
    
            if (chartInstance) {
              chartInstance.setOption(YFilledHide);
            }
          }
          else{
            const YFilledShow = {
              yAxis: {
                show: true,
                type: "value",
                name: yAxisName,
                nameLocation: "middle",
                nameGap: nameGapYAxis(maxDigits),
              },
            };
    
            if (chartInstance) {
              chartInstance.setOption(YFilledShow);
            }
          }
    };
    const onEvents = {
        'click': handleChartClick, // chart click event
        'legendselectchanged': handleLegendSelectChanged, // legend click event
    };
    return (
        <div>
            <div >
                <ReactECharts option={getOption()} onEvents={events ? onEvents : ""} onChartReady={handleChartReady}/>
            </div></div>
    );
};
VerticalStack.displayName="VerticalStack";
function areEqual(prevProps, nextProps) {
    if (_.isEqual(prevProps.ChartData, nextProps.ChartData) &&
        _.isEqual(prevProps.displayData, nextProps.displayData) &&
        _.isEqual(prevProps.barColors, nextProps.barColors) &&
        _.isEqual(prevProps.legendShow, nextProps.legendShow) &&
        _.isEqual(prevProps.barLabel, nextProps.barLabel) &&
        _.isEqual(prevProps.scrollBarHeight, nextProps.scrollBarHeight) &&
        _.isEqual(prevProps.spaceBottom, nextProps.spaceBottom) &&
        _.isEqual(prevProps.spaceLeft, nextProps.spaceLeft) &&
        _.isEqual(prevProps.spaceRight, nextProps.spaceRight) &&
        _.isEqual(prevProps.spaceTop, nextProps.spaceTop) &&
        _.isEqual(prevProps.yAxisName, nextProps.yAxisName) &&
        _.isEqual(prevProps.xAxisName, nextProps.xAxisName)) {
        return true;
    } else {
        return false;
    }
}

VerticalStack.propTypes = {
  barColors: PropTypes.arrayOf(PropTypes.string).isRequired,
  barLabel: PropTypes.bool,
  ChartData: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  color: PropTypes.arrayOf(PropTypes.string),
  displayData: PropTypes.arrayOf(PropTypes.string).isRequired,
  events: PropTypes.bool,
  isScroll: PropTypes.bool,
  isScrollEndLimit: PropTypes.number,
  isScrollStartLimit: PropTypes.number,
  scrollBarHeight: PropTypes.number,
  labelConfig: PropTypes.object,
  legendShow: PropTypes.bool,
  onChartClick: PropTypes.func,
  scrollBarBottomAlign: PropTypes.number,
  spaceBottom: PropTypes.number,
  spaceLeft: PropTypes.number,
  spaceRight: PropTypes.number,
  spaceTop: PropTypes.number,
  toolTipConfig: PropTypes.object,
  xAxisData: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])).isRequired,
  xAxisName: PropTypes.string,
  yAxisName: PropTypes.string,
};

export default React.memo(VerticalStack, areEqual);


